/**
 * Album art gradient generator — deterministic, design-system-only colours.
 *
 * Token source: client/src/theme/classy/_variables.scss (authoritative).
 * Only light variants (100–300 shades) of each hue family are included.
 * Grey is excluded — achromatic values produce muddy gradients.
 *
 * Hex values are hard-coded here for the same reason as helpers/chartPalette.ts:
 * SCSS variables do not resolve at runtime.
 *
 * CONFLICT NOTE: $px-color-gray-* (American spelling) in _variables.scss are
 * placeholder stubs set to #ff0000. Only the canonical $px-color-grey-*
 * (British spelling) scale is real — these are NOT included here.
 */

export type AlbumGradientResult = {
	/** CSS gradient value — assign to `background-image` or use `style` */
	gradient: string;
	/** Ready-to-spread inline style object */
	style: { backgroundImage: string };
	/**
	 * Complementary dark shade (700) of the first gradient stop's hue family.
	 * Use this for any overlay text on the gradient surface.
	 */
	textColor: string;
	/** Hex stops used — useful for debugging and tests */
	stops: string[];
};

// ─── Colour palette ────────────────────────────────────────────────────────────
// 9 hue families × 2 light shades (100, 200) = 18 entries.
// Hue angles (degrees) are pre-computed to avoid runtime hex→HSL conversion.
//
// Source: client/src/theme/classy/_variables.scss

type PaletteEntry = { hex: string; hue: number; name: string; dark: string };

const PALETTE: PaletteEntry[] = [
	// blue — ~205° — dark: blue-700 #0040ff
	{ hex: '#d5efff', hue: 203, name: 'blue-100', dark: '#0040ff' },
	{ hex: '#b3dfff', hue: 205, name: 'blue-200', dark: '#0040ff' },
	// yellow — ~58° — dark: yellow-700 #a06f08
	{ hex: '#fefec3', hue: 60, name: 'yellow-100', dark: '#a06f08' },
	{ hex: '#fdf98b', hue: 58, name: 'yellow-200', dark: '#a06f08' },
	// green — ~77° — dark: green-700 #548605
	{ hex: '#f1ffc7', hue: 75, name: 'green-100', dark: '#548605' },
	{ hex: '#e1ff95', hue: 77, name: 'green-200', dark: '#548605' },
	// teal — ~135° — dark: teal-700 #05902b
	{ hex: '#d7ffe1', hue: 135, name: 'teal-100', dark: '#05902b' },
	{ hex: '#b2ffc5', hue: 135, name: 'teal-200', dark: '#05902b' },
	// aqua — ~180° — dark: aqua-700 #0b8593
	{ hex: '#cefffe', hue: 179, name: 'aqua-100', dark: '#0b8593' },
	{ hex: '#a3fefe', hue: 180, name: 'aqua-200', dark: '#0b8593' },
	// purple — ~256° — dark: purple-700 #633fb1
	{ hex: '#f0ecfb', hue: 256, name: 'purple-100', dark: '#633fb1' },
	{ hex: '#e3dcf8', hue: 255, name: 'purple-200', dark: '#633fb1' },
	// maroon — ~351° — dark: maroon-700 #b41743
	{ hex: '#fee5e8', hue: 353, name: 'maroon-100', dark: '#b41743' },
	{ hex: '#fccfd6', hue: 351, name: 'maroon-200', dark: '#b41743' },
	// red — ~9° — dark: red-700 #c9270c
	{ hex: '#ffe4df', hue: 9, name: 'red-100', dark: '#c9270c' },
	{ hex: '#ffcdc5', hue: 8, name: 'red-200', dark: '#c9270c' },
	// orange — ~37° — dark: orange-700 #bc5012
	{ hex: '#fef0d6', hue: 39, name: 'orange-100', dark: '#bc5012' },
	{ hex: '#fbddad', hue: 37, name: 'orange-200', dark: '#bc5012' },
];

// ─── Constants ─────────────────────────────────────────────────────────────────

/** Minimum hue angle separation between any two colour stops (degrees). */
const MIN_HUE_DISTANCE = 30;

type GradientType = 'linear' | 'radial';

const LINEAR_ANGLES = [
	'135deg',
	'45deg',
	'90deg',
	'0deg',
	'180deg',
	'225deg',
	'315deg',
	'270deg',
] as const;

const RADIAL_ORIGINS = [
	'circle at 8% 8%',
	'ellipse at 92% 8%',
	'circle at 50% 50%',
	'ellipse at 8% 92%',
	'circle at 92% 92%',
] as const;

// ─── Seeded PRNG ───────────────────────────────────────────────────────────────

/** djb2 XOR variant — maps a string seed to a 32-bit unsigned integer. */
function hashSeed(seed: string): number {
	let h = 5381;
	for (let i = 0; i < seed.length; i++) {
		h = ((h << 5) + h) ^ seed.charCodeAt(i);
		h = h >>> 0;
	}
	return h;
}

/**
 * Linear congruential generator — returns a function that produces
 * uniformly distributed floats in [0, 1) from a deterministic sequence.
 * Parameters are the Numerical Recipes constants (well-tested).
 */
function makeLcg(seed: number): () => number {
	let s = seed >>> 0;
	return () => {
		s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
		return s / 0x100000000;
	};
}

// ─── Colour math ───────────────────────────────────────────────────────────────

/** Shortest angular distance between two hue values on the colour wheel. */
function hueDist(a: number, b: number): number {
	const d = Math.abs(a - b) % 360;
	return d > 180 ? 360 - d : d;
}

// ─── Stop selection ────────────────────────────────────────────────────────────

function pickStops(rand: () => number, count: number): PaletteEntry[] {
	const stops: PaletteEntry[] = [];

	for (let i = 0; i < count; i++) {
		const startIdx = Math.floor(rand() * PALETTE.length);
		let found = false;
		let relaxed = false;
		let threshold = MIN_HUE_DISTANCE;

		for (let attempt = 0; attempt < PALETTE.length * 2; attempt++) {
			// After one full scan without a match, relax the threshold
			if (!relaxed && attempt === PALETTE.length) {
				threshold = MIN_HUE_DISTANCE / 2;
				relaxed = true;
			}

			const candidate = PALETTE[(startIdx + attempt) % PALETTE.length];
			const hueOk = stops.every((s) => hueDist(s.hue, candidate.hue) >= threshold);
			const notDupe = !stops.some((s) => s.hex === candidate.hex);

			if (hueOk && notDupe) {
				stops.push(candidate);
				found = true;
				break;
			}
		}

		if (!found) {
			// Last-resort fallback: any unused entry
			const fallback =
				PALETTE.find((e) => !stops.some((s) => s.hex === e.hex)) ??
				PALETTE[i % PALETTE.length];
			stops.push(fallback);
		}
	}

	return stops;
}

// ─── Gradient builder ──────────────────────────────────────────────────────────

function buildGradient(
	stops: PaletteEntry[],
	gradType: GradientType,
	rand: () => number,
): { gradient: string; centerStop: PaletteEntry } {
	const hexes = stops.map((s) => s.hex);
	const count = stops.length;

	// Variable midpoint for 3-stop gradients: anywhere from 30%–70%
	const mid = count === 3 ? 30 + Math.floor(rand() * 41) : 50;
	const stopStr =
		count === 3
			? `${hexes[0]} 0%, ${hexes[1]} ${mid}%, ${hexes[2]} 100%`
			: `${hexes[0]} 0%, ${hexes[1]} 100%`;

	if (gradType === 'radial') {
		const origin = RADIAL_ORIGINS[Math.floor(rand() * RADIAL_ORIGINS.length)];
		// Corner origins: last stop dominates the visual centre of the element.
		// Centre origin (50% 50%): first stop is literally at centre.
		const isCenterOrigin = origin === 'circle at 50% 50%';
		const centerStop = isCenterOrigin ? stops[0] : stops[count - 1];
		return { gradient: `radial-gradient(${origin}, ${stopStr})`, centerStop };
	}

	const angle = LINEAR_ANGLES[Math.floor(rand() * LINEAR_ANGLES.length)];
	// Linear: stop[1] is the explicit midpoint (3-stop) or 50% blend (2-stop).
	const centerStop = stops[1];
	return { gradient: `linear-gradient(${angle}, ${stopStr})`, centerStop };
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Generates a deterministic CSS gradient for use as album art background.
 *
 * The generator is seeded — the same input always produces the same gradient.
 * Colour stops are drawn exclusively from the design system light palette
 * (100–200 shades) and are enforced to have a minimum hue distance of 30°
 * to prevent muddy, near-invisible gradients.
 *
 * Gradient types: linear (50%), radial (50%).
 * 3-stop gradients use a variable midpoint (30–70%) for additional variety.
 *
 * @param seed - Any string (song ID, title, etc.). Same input → same output.
 * @returns Gradient string, ready-to-use inline style, complementary text
 *          colour (700 shade of the centre stop's family), and hex stops for debugging.
 *
 * @example
 * const { style, textColor } = generateAlbumGradient(song.id);
 * <div style={style}>
 *   <span style={{ color: textColor }}>{song.title}</span>
 * </div>
 */
export function generateAlbumGradient(seed: string): AlbumGradientResult {
	const rand = makeLcg(hashSeed(seed));

	const stopCount = rand() < 0.5 ? 2 : 3;

	const typeRoll = rand();
	const gradType: GradientType = typeRoll < 0.5 ? 'linear' : 'radial';

	const stops = pickStops(rand, stopCount);
	const hexes = stops.map((s) => s.hex);

	const { gradient, centerStop } = buildGradient(stops, gradType, rand);

	// Text colour: 700 shade of the centre stop's hue family — derived from
	// whichever colour is visually dominant at the middle of the element.
	const textColor = centerStop.dark;

	return {
		gradient,
		style: { backgroundImage: gradient },
		textColor,
		stops: hexes,
	};
}
